import React, { useState, useEffect } from 'react';
import { FaFileContract, FaEnvelope } from 'react-icons/fa';

const LAST_UPDATED = 'March 1, 2026';
const COMPANY = 'Fitnession Health Tech Pvt. Ltd.';
const EMAIL = 'legal@fitnession.fit';

const sections = [
  { id: 'acceptance', title: '1. Acceptance of Terms' },
  { id: 'services', title: '2. Our Services' },
  { id: 'accounts', title: '3. User Accounts' },
  { id: 'subscriptions', title: '4. Subscriptions & Payments' },
  { id: 'cancellation', title: '5. Cancellation & Refunds' },
  { id: 'ai-coaching', title: '6. Coaching Disclaimer' },
  { id: 'user-conduct', title: '7. Acceptable Use' },
  { id: 'content', title: '8. User Content' },
  { id: 'ip', title: '9. Intellectual Property' },
  { id: 'liability', title: '10. Limitation of Liability' },
  { id: 'termination', title: '11. Termination' },
  { id: 'governing-law', title: '12. Governing Law' },
  { id: 'contact', title: '13. Contact' },
];

const Terms = () => {
  const [activeSection, setActiveSection] = useState('acceptance');

  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(e => { if (e.isIntersecting) setActiveSection(e.target.id); });
      },
      { rootMargin: '-20% 0px -70% 0px' }
    );
    sections.forEach(s => {
      const el = document.getElementById(s.id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  return (
    <div className="bg-white min-h-screen">
      {/* Hero */}
      <div className="bg-gradient-to-br from-gray-900 to-gray-800 text-white py-14 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center justify-center w-14 h-14 bg-white/10 rounded-2xl mb-4">
            <FaFileContract className="text-2xl text-green-400" />
          </div>
          <h1 className="text-4xl font-bold mb-3">Terms of Service</h1>
          <p className="text-gray-300">{COMPANY} · Last updated: {LAST_UPDATED}</p>
          <p className="text-gray-400 text-sm mt-2">Applies to the Fitnession platform at fitnession.fit</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex gap-12">

          {/* Sticky TOC */}
          <aside className="hidden lg:block w-64 flex-shrink-0">
            <div className="sticky top-24">
              <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">Contents</p>
              <nav className="space-y-1">
                {sections.map(s => (
                  <a
                    key={s.id}
                    href={`#${s.id}`}
                    className={`block text-sm py-1.5 px-3 rounded-lg transition-colors ${
                      activeSection === s.id
                        ? 'bg-green-50 text-green-700 font-medium'
                        : 'text-gray-500 hover:text-gray-800 hover:bg-gray-50'
                    }`}
                  >
                    {s.title}
                  </a>
                ))}
              </nav>
            </div>
          </aside>

          {/* Content */}
          <article className="flex-1 min-w-0">

            <section id="acceptance" className="mb-10 scroll-mt-24">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Acceptance of Terms</h2>
              <p className="text-gray-600 leading-relaxed">
                By accessing or using the Fitnession platform ("Platform") — including the website at fitnession.fit, the Fitnession mobile application, and any related services — you ("User", "you") agree to be bound by these Terms of Service ("Terms") and our Privacy Policy. If you do not agree, you must not use the Platform.
              </p>
              <p className="text-gray-600 leading-relaxed mt-3">
                {COMPANY} ("Fitnession", "we", "us") reserves the right to update these Terms at any time. Continued use after changes constitutes acceptance. We will notify you of material changes by email.
              </p>
              <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 mt-4">
                <p className="text-sm text-yellow-800">
                  <strong>GLP-1 Medical Program:</strong> If you use our GLP-1 medical weight loss program at glp1.fitnession.fit, separate Terms of Service apply. Please read the <a href="https://glp1.fitnession.fit/terms" className="underline font-medium">GLP-1 Terms of Service</a>.
                </p>
              </div>
            </section>

            <section id="services" className="mb-10 scroll-mt-24">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Our Services</h2>
              <p className="text-gray-600 mb-4">Fitnession provides a digital fitness and nutrition coaching platform, including:</p>
              <ul className="list-disc list-inside space-y-2 text-gray-600">
                <li>Personalised nutrition coaching and meal planning</li>
                <li>Fitness programme design and progress tracking</li>
                <li>Personalised coaching conversations</li>
                <li>Weekly check-ins and progress photo tracking</li>
                <li>Access to a library of blog articles, fitness calculators, and educational content</li>
                <li>Communication tools between clients, coaches, and nutritionists</li>
              </ul>
              <p className="text-gray-600 mt-4">
                Fitnession is a <strong>fitness and wellness coaching platform</strong>, not a medical service provider. Content and coaching on fitnession.fit does not constitute medical advice, diagnosis, or treatment. Consult a qualified physician before starting any exercise or diet programme.
              </p>
            </section>

            <section id="accounts" className="mb-10 scroll-mt-24">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">3. User Accounts</h2>
              <ul className="list-disc list-inside space-y-2 text-gray-600">
                <li>You must be at least 18 years old to create an account.</li>
                <li>You are responsible for maintaining the confidentiality of your login credentials.</li>
                <li>You must provide accurate and complete information during registration and keep it updated.</li>
                <li>You are responsible for all activity that occurs under your account.</li>
                <li>Notify us immediately at {EMAIL} if you suspect unauthorised access to your account.</li>
                <li>Each account is for individual use only — sharing accounts is prohibited.</li>
              </ul>
            </section>

            <section id="subscriptions" className="mb-10 scroll-mt-24">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Subscriptions & Payments</h2>

              <h3 className="text-lg font-semibold text-gray-800 mb-2">4.1 Subscription Plans</h3>
              <p className="text-gray-600 mb-3">Fitnession offers monthly and multi-month subscription plans as described on our Pricing page. Prices are in Indian Rupees (₹) and include applicable GST.</p>

              <h3 className="text-lg font-semibold text-gray-800 mb-2">4.2 Billing</h3>
              <ul className="list-disc list-inside space-y-1.5 text-gray-600 mb-3">
                <li>Subscriptions auto-renew at the end of each billing period unless cancelled.</li>
                <li>Payment is processed via Razorpay at the start of each billing period.</li>
                <li>We will send a payment receipt to your registered email after each charge.</li>
                <li>Failed payments may result in temporary suspension of your account.</li>
              </ul>

              <h3 className="text-lg font-semibold text-gray-800 mb-2">4.3 Price Changes</h3>
              <p className="text-gray-600">We reserve the right to change subscription prices. We will give you at least 30 days' notice before any price increase takes effect. Continued use after the effective date constitutes acceptance.</p>
            </section>

            <section id="cancellation" className="mb-10 scroll-mt-24">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Cancellation & Refunds</h2>

              <h3 className="text-lg font-semibold text-gray-800 mb-2">5.1 Cancellation</h3>
              <p className="text-gray-600 mb-3">You may cancel your subscription at any time from your dashboard or by emailing {EMAIL}. Cancellation takes effect at the end of the current billing period — you retain access until then.</p>

              <h3 className="text-lg font-semibold text-gray-800 mb-2">5.2 Refunds</h3>
              <p className="text-gray-600 mb-3">Fitnession operates a no-refund policy for completed billing periods, except in the following cases:</p>
              <ul className="list-disc list-inside space-y-1.5 text-gray-600 mb-3">
                <li><strong>Technical failure:</strong> If you are unable to access paid features due to a verifiable platform error, you are entitled to a pro-rated refund for the affected period.</li>
                <li><strong>Double charge:</strong> Duplicate payments will be refunded within 7 business days.</li>
                <li><strong>Cancellation within 24 hours:</strong> If you cancel within 24 hours of your first subscription payment and have not used any coaching sessions, you may request a full refund.</li>
              </ul>
              <p className="text-gray-600">All refund requests must be submitted to {EMAIL} within 7 days of the charge in question.</p>
            </section>

            <section id="ai-coaching" className="mb-10 scroll-mt-24">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Coaching Disclaimer</h2>
              <div className="bg-orange-50 border border-orange-200 rounded-xl p-5">
                <p className="text-gray-700 leading-relaxed mb-3">
                  Fitnession provides personalised nutrition and fitness coaching guidance. You acknowledge and agree that:
                </p>
                <ul className="list-disc list-inside space-y-2 text-gray-700">
                  <li>Coaching responses are for informational and motivational purposes only.</li>
                  <li>Coaching does not constitute medical advice, diagnosis, or treatment.</li>
                  <li>Guidance may occasionally need to be adapted to your specific circumstances.</li>
                  <li>You should not rely on coaching as a substitute for advice from a qualified medical professional.</li>
                  <li>Fitnession is not liable for any harm arising from reliance on coaching content.</li>
                </ul>
              </div>
            </section>

            <section id="user-conduct" className="mb-10 scroll-mt-24">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Acceptable Use</h2>
              <p className="text-gray-600 mb-3">You agree not to use the Platform to:</p>
              <ul className="list-disc list-inside space-y-2 text-gray-600">
                <li>Post false, misleading, or harmful content</li>
                <li>Harass, abuse, or intimidate coaches, doctors, or other users</li>
                <li>Attempt to gain unauthorised access to any part of the Platform</li>
                <li>Scrape, copy, or redistribute Platform content without written permission</li>
                <li>Impersonate any person or entity</li>
                <li>Upload malware or any harmful code</li>
                <li>Violate any applicable Indian or international law</li>
              </ul>
              <p className="text-gray-600 mt-3">Violation may result in immediate account termination without refund.</p>
            </section>

            <section id="content" className="mb-10 scroll-mt-24">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">8. User Content</h2>
              <p className="text-gray-600 mb-3">When you upload progress photos, check-in data, messages, or other content ("User Content"), you grant Fitnession a limited, non-exclusive licence to store and use that content solely to deliver our services to you.</p>
              <p className="text-gray-600">You retain ownership of your User Content. We will never use your progress photos or personal data in marketing materials without your explicit written consent.</p>
            </section>

            <section id="ip" className="mb-10 scroll-mt-24">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Intellectual Property</h2>
              <p className="text-gray-600">All Platform content — including the website design, logos, software, meal plan templates, workout programmes, and blog articles — is the intellectual property of {COMPANY} and is protected under Indian copyright and trademark law. You may not reproduce, distribute, or create derivative works without our prior written consent.</p>
            </section>

            <section id="liability" className="mb-10 scroll-mt-24">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Limitation of Liability</h2>
              <p className="text-gray-600 mb-3">To the fullest extent permitted by applicable law:</p>
              <ul className="list-disc list-inside space-y-2 text-gray-600">
                <li>Fitnession's total liability to you for any claim arising from your use of the Platform shall not exceed the total amount you paid us in the 3 months preceding the claim.</li>
                <li>We are not liable for indirect, incidental, consequential, or punitive damages.</li>
                <li>We are not liable for any injury, health issue, or adverse outcome resulting from following fitness or nutrition guidance.</li>
                <li>The Platform is provided "as is" without warranty of any kind.</li>
              </ul>
            </section>

            <section id="termination" className="mb-10 scroll-mt-24">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Termination</h2>
              <p className="text-gray-600 mb-3">We reserve the right to suspend or terminate your account at our discretion if you:</p>
              <ul className="list-disc list-inside space-y-1.5 text-gray-600 mb-3">
                <li>Violate these Terms or our Acceptable Use policy</li>
                <li>Provide false information during registration or assessment</li>
                <li>Engage in fraudulent activity</li>
                <li>Fail to pay subscription fees after a 7-day grace period</li>
              </ul>
              <p className="text-gray-600">Upon termination, your access to the Platform ceases and any unpaid amounts become immediately due. Data deletion follows our retention policy in the Privacy Policy.</p>
            </section>

            <section id="governing-law" className="mb-10 scroll-mt-24">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">12. Governing Law</h2>
              <p className="text-gray-600 mb-3">These Terms are governed by the laws of India. Any disputes shall be subject to the exclusive jurisdiction of the courts in Pune, Maharashtra, India.</p>
              <p className="text-gray-600">We encourage you to contact us at {EMAIL} before initiating any legal proceedings — most issues can be resolved amicably.</p>
            </section>

            <section id="contact" className="mb-10 scroll-mt-24">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">13. Contact</h2>
              <div className="bg-gray-50 rounded-2xl p-6">
                <p className="font-bold text-gray-900 mb-1">{COMPANY}</p>
                <p className="text-gray-600 text-sm mb-3">Legal / Terms enquiries</p>
                <p className="flex items-center gap-2 text-sm text-gray-600"><FaEnvelope className="text-green-500" /> {EMAIL}</p>
              </div>
            </section>

          </article>
        </div>
      </div>
    </div>
  );
};

export default Terms;
